import * as React from "react";

import {
  TimeAnchor,
  createAnchor,
  formatAnchorTime,
  parseAnchorInput,
} from "@/shared/components/RobotoMarkdown/timeFormat";
import { useFile } from "@/shared/domain/files/hooks";
import {
  useDatasetTopicTimeBounds,
  useFileTopicTimeBounds,
} from "@/shared/domain/topics/hooks";
import { Button, Popover, TextInput } from "@/shared/foundation/primitives";
import {
  CURRENT_TIME_NS_FIELD,
  EARLIEST_TIME_NS_FIELD,
  getLiveVisualizerState,
  useViewingContext,
} from "@/shared/services";

import { AIAgentThreadContext } from "../AIAgentThreadContext";

import styles from "./AnchorPickerPopover.module.css";

/**
 * Each suggested dataset or file costs a time-bounds query while the popover
 * is open, so only the first few in context are offered. A thread rarely holds
 * more, and the free-text field covers the rest.
 */
const MAX_SUGGESTED_PER_KIND = 5;

const INVALID_INPUT_MESSAGE =
  "Enter epoch nanoseconds (up to 19 digits), seconds.nanos, or an ISO 8601 date.";

const VISUALIZER_START_LABEL = "Visualizer: start of loaded data";
const VISUALIZER_PLAYHEAD_LABEL = "Visualizer: playhead";

// 19 digits spans every epoch-ns instant up to the year 2286; the visualizer
// publishes both of its instants as decimal strings because epoch nanoseconds
// exceed Number.MAX_SAFE_INTEGER. Validated here rather than trusted: `BigInt`
// reads "" as 0n and accepts negatives, either of which would offer a 1970
// instant as a suggestion.
const EPOCH_NS_DIGITS = /^\d{1,19}$/;

interface AnchorPickerPopoverProps {
  /** Controlled so the host modal can relax its focus trap while open. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Text of the button that opens the picker ("Change…", "Pick…"). */
  triggerLabel: string;
  onPick: (anchor: TimeAnchor) => void;
}

/**
 * Lets the user set a thread's t=0: a suggested start time (a dataset or file
 * in the chat context, or an instant the open visualizer is showing) or a typed
 * instant. Whatever they choose is a fixed instant — this picker never
 * subscribes the thread to anything that moves. A reader who wants their
 * timestamps to track the visualizer picks "Follow the visualizer" instead.
 *
 * The popup body is unmounted while closed, so suggestion queries run only
 * while the user is looking at them, typed text resets on each open, and the
 * visualizer instants are re-snapshotted each time it opens.
 */
export function AnchorPickerPopover({
  open,
  onOpenChange,
  triggerLabel,
  onPick,
}: AnchorPickerPopoverProps) {
  const handlePick = (anchor: TimeAnchor) => {
    onPick(anchor);
    onOpenChange(false);
  };

  return (
    <Popover
      trigger={
        <Button variant="outlined" size="small">
          {triggerLabel}
        </Button>
      }
      open={open}
      onOpenChange={onOpenChange}
      align="center"
      aria-label="Choose t=0"
      className={styles.popup}
    >
      <PickerBody onPick={handlePick} onClose={() => onOpenChange(false)} />
    </Popover>
  );
}

interface PickerBodyProps {
  onPick: (anchor: TimeAnchor) => void;
  onClose: () => void;
}

function PickerBody({ onPick, onClose }: PickerBodyProps) {
  // The picker also renders outside a thread provider (the modal is reachable
  // wherever the sidebar is); with no context there are simply no dataset or
  // file suggestions, so the throwing `useAIAgentThread` is bypassed.
  const thread = React.useContext(AIAgentThreadContext);
  const datasetIds =
    thread?.context.dataset_ids.slice(0, MAX_SUGGESTED_PER_KIND) ?? [];
  const fileIds =
    thread?.context.file_ids.slice(0, MAX_SUGGESTED_PER_KIND) ?? [];
  const datasetNames = thread?.contextDisplayNames.datasets ?? {};
  const fileNames = thread?.contextDisplayNames.files ?? {};

  const visualizerInstants = useVisualizerSnapshot();

  const [text, setText] = React.useState("");
  const trimmed = text.trim();
  const parsed = parseAnchorInput(trimmed);

  const commitTyped = () => {
    if (parsed === undefined) {
      return;
    }
    onPick(createAnchor(parsed, formatAnchorTime(parsed)));
  };

  // The popup is portaled outside the settings dialog's DOM but inside its
  // React tree, so an Escape here would also reach the MUI Dialog and close
  // it. Stop it and close only the picker.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
    }
  };

  return (
    <div className={styles.body} onKeyDown={handleKeyDown}>
      {/* Rows drop out when their source has no timestamped topics, which the
          dataset and file rows learn only once loaded, so the section hides
          itself in CSS while it holds none. */}
      <section className={styles.suggestions} aria-label="Suggested">
        <h3 className={styles.sectionTitle}>Suggested</h3>
        {visualizerInstants.map(({ label, ns }) => (
          <SuggestionRow key={label} label={label} ns={ns} onPick={onPick} />
        ))}
        {datasetIds.map((datasetId) => (
          <DatasetSuggestion
            key={datasetId}
            datasetId={datasetId}
            name={datasetNames[datasetId] ?? datasetId}
            onPick={onPick}
          />
        ))}
        {fileIds.map((fileId) => (
          <FileSuggestion
            key={fileId}
            fileId={fileId}
            name={fileNames[fileId] ?? fileId}
            onPick={onPick}
          />
        ))}
      </section>

      <section className={styles.section} aria-label="Enter a time">
        <h3 className={styles.sectionTitle}>Enter a time</h3>
        <div className={styles.entryRow}>
          <TextInput
            aria-label="t=0 time"
            placeholder="ns, seconds.nanos, or ISO 8601"
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                commitTyped();
              }
            }}
            autoComplete="off"
            spellCheck={false}
          />
          <Button
            variant="contained"
            size="small"
            disabled={parsed === undefined}
            onClick={commitTyped}
          >
            Use
          </Button>
        </div>
        {/* The parsed instant is shown before the user commits: bare digits
            are nanoseconds, so a pasted epoch-seconds value reads as 1970
            here instead of silently anchoring the thread there. */}
        {parsed !== undefined && (
          <p className={styles.confirmation}>
            = <span className={styles.time}>{formatAnchorTime(parsed)}</span>
          </p>
        )}
        {trimmed !== "" && parsed === undefined && (
          <p className={styles.error} role="alert">
            {INVALID_INPUT_MESSAGE}
          </p>
        )}
      </section>
    </div>
  );
}

interface SuggestedInstant {
  label: string;
  ns: bigint;
}

/**
 * The open visualizer's two interesting instants — where its loaded data
 * starts, and where its playhead is — read once, when the picker opens.
 *
 * A snapshot on purpose. Anchors set from here are fixed instants like any
 * other, and the playhead in particular advances at tick rate: a live read
 * would offer a row whose time changed under the cursor and pin whatever
 * moment the click happened to land on.
 */
function useVisualizerSnapshot(): SuggestedInstant[] {
  const viewingContext = useViewingContext();
  // Deliberately not in the dependency list of anything: the initializer runs
  // on the picker's first render and never again, which is what makes this a
  // snapshot rather than a subscription.
  const visualizerState = viewingContext.visualizer?.state;
  const [snapshot] = React.useState<SuggestedInstant[]>(() => {
    if (visualizerState === undefined) {
      return [];
    }
    const instants: SuggestedInstant[] = [];
    const earliest = epochNs(visualizerState[EARLIEST_TIME_NS_FIELD]);
    if (earliest !== undefined) {
      instants.push({ label: VISUALIZER_START_LABEL, ns: earliest });
    }
    const playhead = epochNs(getLiveVisualizerState()?.[CURRENT_TIME_NS_FIELD]);
    if (playhead !== undefined) {
      instants.push({ label: VISUALIZER_PLAYHEAD_LABEL, ns: playhead });
    }
    return instants;
  });
  return snapshot;
}

function epochNs(published: unknown): bigint | undefined {
  return typeof published === "string" && EPOCH_NS_DIGITS.test(published)
    ? BigInt(published)
    : undefined;
}

interface SuggestionRowProps {
  label: string;
  ns: bigint;
  onPick: (anchor: TimeAnchor) => void;
}

function SuggestionRow({ label, ns, onPick }: SuggestionRowProps) {
  return (
    <button
      type="button"
      className={styles.suggestion}
      onClick={() => onPick(createAnchor(ns, label))}
    >
      <span className={styles.suggestionLabel}>{label}</span>
      <span className={styles.time}>{formatAnchorTime(ns)}</span>
    </button>
  );
}

interface StartOfSuggestionProps {
  startTime: bigint | null | undefined;
  name: string;
  onPick: (anchor: TimeAnchor) => void;
}

/**
 * The "Start of <name>" row the dataset and file suggestions share; they
 * differ only in which time-bounds query they run. The row renders nothing
 * while the bounds are loading and nothing at all for an entity whose topics
 * carry no timestamps, which the endpoint reports as a null `start_time`.
 */
function StartOfSuggestion({
  startTime,
  name,
  onPick,
}: StartOfSuggestionProps) {
  if (startTime === null || startTime === undefined) {
    return null;
  }
  return (
    <SuggestionRow label={`Start of ${name}`} ns={startTime} onPick={onPick} />
  );
}

interface DatasetSuggestionProps {
  datasetId: string;
  name: string;
  onPick: (anchor: TimeAnchor) => void;
}

function DatasetSuggestion({
  datasetId,
  name,
  onPick,
}: DatasetSuggestionProps) {
  // Mounted only while the popover is open, so enabling the query
  // unconditionally already scopes the fetch to the time the user is looking
  // at it.
  const { data: bounds } = useDatasetTopicTimeBounds(datasetId, true);
  return (
    <StartOfSuggestion
      startTime={bounds?.start_time}
      name={name}
      onPick={onPick}
    />
  );
}

interface FileSuggestionProps {
  fileId: string;
  name: string;
  onPick: (anchor: TimeAnchor) => void;
}

function FileSuggestion({ fileId, name, onPick }: FileSuggestionProps) {
  // The bounds query is keyed by file version, which the chat context does
  // not carry, so the file record is fetched first.
  const { data: file } = useFile(fileId);
  const { data: bounds } = useFileTopicTimeBounds(fileId, file?.version ?? 0, {
    isEnabled: file !== undefined,
  });
  return (
    <StartOfSuggestion
      startTime={bounds?.start_time}
      name={name}
      onPick={onPick}
    />
  );
}
